import { jsx as _jsx } from "react/jsx-runtime";
import _toConsumableArray from "/app/store-locator/node_modules/next/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import Opening from 'components/restaurant/opening';
import { RestaurantStatus } from 'core/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { theme } from 'src/template';
import styled from 'styled-components';
import { zoomToCountry, zoomToLocation } from 'utils/geolocation';
import { config } from '../../config';
var loader = new Loader({
  apiKey: config.googleApiKey,
  version: 'weekly',
  libraries: ['places']
});

var Map = function Map(_ref) {
  var markers = _ref.markers,
      location = _ref.location,
      onMarkerClick = _ref.onMarkerClick,
      focused = _ref.focused,
      markerIconUrl = _ref.markerIconUrl;
  var markersInfo = useRef();
  var oldMarkers = useRef();

  var _useState = useState(),
      map = _useState[0],
      setMap = _useState[1];

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  useEffect(function () {
    loader.load().then(function (google) {
      var isMobile = window.matchMedia('only screen and (max-width: 750px)').matches;
      var mapOptions = {
        center: {
          lat: 48,
          lng: 2
        },
        zoom: 4,
        streetViewControl: false,
        gestureHandling: isMobile ? 'none' : 'auto'
      };

      if (config.mapId) {
        mapOptions.mapTypeId = config.mapId;
      }

      setMap(new google.maps.Map(document.getElementById('map'), mapOptions));
      zoomToCountry(map, 'france');
    })["catch"](function (e) {
      console.error(e);
    });
  }, []); // hack to close marker

  if (markersInfo.current) {
    oldMarkers.current = _toConsumableArray(markersInfo.current);
  }

  markersInfo.current = useMemo(function () {
    return createMarkers(markers, onMarkerClick, map);
  }, [markers, onMarkerClick, map]);
  useEffect(function () {
    if (location && map) {
      zoomToLocation(map, location);
    }
  }, [location, map]);
  useEffect(function () {
    if (markersInfo.current && focused) {
      if (oldMarkers.current) {
        oldMarkers.current.forEach(function (marker) {
          marker.close();
        });
      }

      markersInfo.current.forEach(function (marker) {
        marker.close();
      });
      var focusedMarker = markersInfo.current.find(function (marker) {
        return marker.id === focused;
      });

      if (map) {
        zoomToLocation(map, focusedMarker.coord, 7);
      }

      focusedMarker.openWindowInfoWindow();
    }
  }, [focused]);

  function createMarkers(locations, onMarkerClick, map) {
    if (!locations || !onMarkerClick) return [];
    var markers = [];
    var markersInfo = locations.map(function (_ref2) {
      var coord = _ref2.coord,
          label = _ref2.label,
          banner = _ref2.banner,
          address = _ref2.address,
          id = _ref2.id,
          slug = _ref2.slug,
          distance = _ref2.distance,
          opening = _ref2.opening,
          status = _ref2.status;
      var marker = new google.maps.Marker({
        position: {
          lat: coord[0],
          lng: coord[1]
        },
        icon: {
          url: markerIconUrl,
          size: new google.maps.Size(37, 37),
          scaledSize: new google.maps.Size(37, 37)
        }
      });
      var upcoming = status === RestaurantStatus.Register;
      var openingHtmlString = ReactDOMServer.renderToString( /*#__PURE__*/_jsx(Opening, {
        opening: opening,
        upcoming: upcoming
      }));
      var bannerHtmlString = ReactDOMServer.renderToString( /*#__PURE__*/_jsx("div", {
        className: "picture",
        style: {
          backgroundImage: "url(".concat(upcoming ? '/image/upcoming-background.png' : banner, ")"),
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        },
        children: upcoming && /*#__PURE__*/_jsx("p", {
          className: "upcoming",
          children: t('restaurant.upcoming')
        })
      }));
      var contentString = "<div id=\"content\">\n      ".concat(bannerHtmlString, "\n      <div class='details'>\n      <h1 class='title'>").concat(label, "</h1>\n      <div class='address'>").concat(address, "</div>\n      ").concat(openingHtmlString, "\n      <div class='bottom'>\n      <a href='restaurant/").concat(slug, "'>").concat(t('restaurant.moreInfo'), "</a>\n      ").concat(distance ? "<div class='distance'> <svg width=\"16\" height=\"16\" viewBox=\"0 0 16 16\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n      <path d=\"M12.2892 2.66663C12.5703 2.68094 12.7408 2.9431 12.6366 3.21861C12.4409 3.73688 12.2401 4.25355 12.0412 4.77054C10.9837 7.52285 9.92621 10.2752 8.8687 13.0275C8.74536 13.3485 8.40943 13.436 8.20449 13.1977C8.15987 13.1458 8.12672 13.0761 8.1111 13.0093C7.75796 11.4914 7.40705 9.97323 7.05965 8.45407C7.03575 8.34908 6.9924 8.3004 6.88372 8.2759C5.37203 7.93644 3.8613 7.59188 2.35058 7.24797C2.13576 7.19897 2.0105 7.06567 2.00062 6.88273C1.99106 6.70075 2.09178 6.56395 2.29576 6.48568C5.55944 5.23218 8.82249 3.97867 12.0858 2.72612C12.1515 2.70099 12.2213 2.68635 12.2892 2.66663Z\" fill=\"#333333\"/>\n      </svg>\n      ".concat(distance, " km</div></div>") : '', "\n        </div>");
      var infoWindow = new google.maps.InfoWindow({
        content: ''
      }); // markers can only be keyboard focusable when they have click listeners
      // open info window when marker is clicked

      marker.addListener('click', function () {
        infoWindow.setContent(contentString);
        infoWindow.open(map, marker);
        onMarkerClick(id);
      });
      infoWindow.addListener('closeclick', function () {
        onMarkerClick(id, true);
        infoWindow.close();
      });
      markers.push(marker);
      return {
        marker: marker,
        infoWindow: infoWindow,
        id: id,
        coord: coord,
        openWindowInfoWindow: function openWindowInfoWindow() {
          infoWindow.setContent(contentString);
          infoWindow.open(map, marker);
        },
        close: function close() {
          infoWindow.close();
        }
      };
    }); // Add a marker clusterer to manage the markers.

    new MarkerClusterer({
      markers: markers,
      map: map,
      renderer: {
        render: render
      }
    });
    return markersInfo;
  }

  return /*#__PURE__*/_jsx(StyledMap, {
    id: "map",
    className: "map"
  });
};

var render = function render(_ref3, stats) {
  var count = _ref3.count,
      position = _ref3.position;
  var color = count > Math.max(5, stats.clusters.markers.mean) ? theme.colors.primary : theme.colors.secondary;
  var svg = window.btoa("\n<svg fill=\"".concat(color, "\" xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 240 240\">\n<circle cx=\"120\" cy=\"120\" opacity=\".6\" r=\"70\" />\n<circle cx=\"120\" cy=\"120\" opacity=\".3\" r=\"90\" />\n<circle cx=\"120\" cy=\"120\" opacity=\".2\" r=\"110\" />\n</svg>")); // create marker using svg icon

  return new google.maps.Marker({
    position: position,
    icon: {
      url: "data:image/svg+xml;base64,".concat(svg),
      scaledSize: new google.maps.Size(45, 45)
    },
    label: {
      text: String(count),
      color: 'rgba(255,255,255,0.9)',
      fontSize: '12px'
    },
    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
  });
};

export default Map;
var StyledMap = styled.div.withConfig({
  displayName: "map__StyledMap",
  componentId: "sc-1fkwqf5-0"
})(["height:240px;width:100%;.gm-style-iw{padding:0;}.gm-ui-hover-effect{transform:translate(-30%,30%) scale(1.4);color:white;}.details{padding:0 6px;}#content{width:200px;height:100%;}.picture{background-repeat:no-repeat;background-size:contain;background-color:#333;height:80px;display:flex;justify-content:center;align-items:center;.upcoming{font-style:normal;font-weight:580;font-size:24px;line-height:28px;text-align:center;letter-spacing:0.005em;text-transform:uppercase;color:", ";margin:0;}}.address{font-size:11px;font-weight:300;}.title{text-transform:uppercase;margin:8px 0 4px;font-size:13px;font-weight:700;}a{font-size:12px;font-weight:400;line-height:17px;text-decoration-line:underline;}.bottom{align-items:center;display:flex;font-size:12px;font-weight:600;justify-content:space-between;line-height:13px;margin:4px 0;}@media screen and (min-width:750px){height:100vh;#content{width:250px;height:100%;}.picture{height:150px;}.distance{display:flex;align-items:center;}.details{padding:12px;}.bottom{font-size:14px;line-height:18px;margin:10px 0;}}"], theme.colors.secondary);